import React, { useState } from 'react';
import { Facebook, Instagram, MapPin, Phone, Mail, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import AboutUsModal from './AboutUsModal';
import ServicesModal from './ServicesModal';
import ComingSoonModal from './ComingSoonModal';

// WhatsApp SVG Icon component
const WhatsAppIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="h-6 w-6"
  >
    <path
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
    />
  </svg>
);

const Footer = () => {
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');

  const handleSocialClick = (platform: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedPlatform(platform);
    setShowComingSoonModal(true);
  };

  return (
    <footer id="footer" className="bg-gray-900 text-white py-12">
      <AboutUsModal 
        isOpen={showAboutModal} 
        onClose={() => setShowAboutModal(false)}
      />
      <ServicesModal
        isOpen={showServicesModal}
        onClose={() => setShowServicesModal(false)}
      />
      <ComingSoonModal
        isOpen={showComingSoonModal}
        onClose={() => setShowComingSoonModal(false)}
        platform={selectedPlatform}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-xl font-semibold mb-4">Aqua Blue Cyprus</h3>
            <p className="text-gray-400 mb-4">
              Your trusted partner for professional pool services in Cyprus.
            </p>
            <button
              onClick={() => setShowAboutModal(true)}
              className="text-cyan-400 hover:text-cyan-300 transition-colors duration-300 flex items-center gap-2 group"
            >
              Learn more about us
              <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
            </button>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-4">Contact</h3>
            <div className="space-y-2">
              <p className="flex items-center text-gray-400">
                <MapPin className="h-5 w-5 mr-2" />
                Paphos, Cyprus
              </p>
              <p className="flex items-center text-gray-400">
                <Phone className="h-5 w-5 mr-2" />
                +357 99 562 552
              </p>
              <p className="flex items-center text-gray-400">
                <Mail className="h-5 w-5 mr-2" />
                info@aquabluecyprus.com
              </p>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => setShowServicesModal(true)}
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Services
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setShowAboutModal(true)}
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  About Us
                </button>
              </li>
              <li>
                <a 
                  href="#contact"
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Contact
                </a>
              </li>
              <li><Link to="/payment-terms" className="text-gray-400 hover:text-white transition-colors duration-300">Payment Terms</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-4">Secure Payments</h3>
            <div className="flex items-center mb-4">
              <Lock className="h-5 w-5 text-cyan-500 mr-2" />
              <span className="text-gray-400">SSL Encrypted Payments</span>
            </div>
            <div className="space-y-4">
              <div className="flex items-center space-x-6">
                <img 
                  src="/visa-logo.png" 
                  width={48}
                  height={48}
                  alt="Visa" 
                  className="h-8 object-contain hover:opacity-90 transition-opacity"
                />
                <img 
                  src="/mastercard-logo.png" 
                  width={48}
                  height={48}
                  alt="Mastercard" 
                  className="h-8 object-contain hover:opacity-90 transition-opacity"
                />
              </div>
              <p className="text-sm text-gray-400">
                Secure payments processed by Stripe
              </p>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400">
              &copy; {new Date().getFullYear()} Aqua Blue Cyprus. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a 
                href="https://wa.me/35799562552" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="group flex items-center gap-2 text-gray-400 hover:text-green-500 transition-all duration-300 ease-in-out"
                aria-label="Contact us on WhatsApp"
              >
                <span className="transform transition-all duration-300 ease-in-out group-hover:scale-110">
                  <WhatsAppIcon />
                </span>
                <span className="overflow-hidden max-w-0 group-hover:max-w-[100px] transition-all duration-500 ease-in-out whitespace-nowrap opacity-0 group-hover:opacity-100">
                  WhatsApp
                </span>
              </a>
              <a 
                href="#" 
                onClick={handleSocialClick('Facebook')}
                className="group flex items-center gap-2 text-gray-400 hover:text-blue-600 transition-all duration-300 ease-in-out"
                aria-label="Follow us on Facebook"
              >
                <span className="transform transition-all duration-300 ease-in-out group-hover:scale-110">
                  <Facebook className="h-6 w-6" />
                </span>
                <span className="overflow-hidden max-w-0 group-hover:max-w-[100px] transition-all duration-500 ease-in-out whitespace-nowrap opacity-0 group-hover:opacity-100">
                  Facebook
                </span>
              </a>
              <a 
                href="https://www.instagram.com/aquabluecyprus/" 
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 text-gray-400 hover:text-pink-600 transition-all duration-300 ease-in-out"
                aria-label="Follow us on Instagram"
              >
                <span className="transform transition-all duration-300 ease-in-out group-hover:scale-110">
                  <Instagram className="h-6 w-6" />
                </span>
                <span className="overflow-hidden max-w-0 group-hover:max-w-[100px] transition-all duration-500 ease-in-out whitespace-nowrap opacity-0 group-hover:opacity-100">
                  Instagram
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;